import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Send, Printer, Trash2 } from "lucide-react";
import { crm, money } from "../lib/crmClient";

export default function CrmInvoiceDetail() {
    const { id } = useParams();
    const [inv, setInv] = useState(null);
    const [client, setClient] = useState(null);
    const [msg, setMsg] = useState("");
    const [sending, setSending] = useState(false);

    const load = () => {
        crm.get(`/crm/invoices/${id}`).then((r) => {
            setInv(r.data);
            crm.get(`/crm/clients/${r.data.client_id}`).then((c) => setClient(c.data));
        });
    };
    useEffect(load, [id]);

    if (!inv) return <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/40">Loading…</div>;

    const send = async () => {
        setSending(true); setMsg("");
        try {
            const { data } = await crm.post(`/crm/invoices/${id}/send`);
            setMsg(`Sent to ${data.to}`);
            load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            setMsg(typeof d === "string" ? d : "Send failed");
        } finally { setSending(false); }
    };

    const remove = async () => {
        if (!window.confirm("Delete this invoice?")) return;
        await crm.delete(`/crm/invoices/${id}`);
        window.location.href = "/crm/invoices";
    };

    const publicUrl = `${window.location.origin}/pay/${inv.public_token}`;

    return (
        <div data-testid="crm-invoice-detail">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">{inv.number} · {inv.kind}</div>
                    <h1 className="font-display uppercase text-4xl md:text-5xl leading-tight tracking-tight mt-2">{inv.title}</h1>
                </div>
                <div className="flex items-center gap-2">
                    <span data-testid="inv-status" className="font-mono text-[10px] tracking-[0.28em] uppercase border border-white/30 px-3 py-1.5">{inv.status}</span>
                    <button onClick={() => window.print()} className="p-2 border border-white/25 hover:border-white transition-colors" aria-label="Print"><Printer className="w-4 h-4" /></button>
                    <button onClick={remove} data-testid="inv-delete" className="p-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 glass border border-white/10 p-6 md:p-8 print:border-black print:bg-white print:text-black">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-white/10">
                                {["Description","Qty","Unit","Amount"].map((h) => (
                                    <th key={h} className="text-left p-3 font-mono text-[9px] tracking-[0.28em] uppercase text-white/40 print:text-black">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {inv.line_items.map((li, i) => (
                                <tr key={i} className="border-b border-white/5">
                                    <td className="p-3 font-mono text-[13px]">{li.description}</td>
                                    <td className="p-3 font-mono text-[13px]">{li.quantity}</td>
                                    <td className="p-3 font-mono text-[13px]">{money(li.unit_price)}</td>
                                    <td className="p-3 font-mono text-[13px] text-right">{money(li.quantity * li.unit_price)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="mt-6 flex flex-col items-end gap-1 font-mono text-[13px]">
                        <div className="flex gap-8"><span className="text-white/60">Subtotal</span><span>{money(inv.subtotal)}</span></div>
                        <div className="flex gap-8"><span className="text-white/60">Tax ({(inv.tax_rate*100).toFixed(2)}%)</span><span>{money(inv.tax_amount)}</span></div>
                        <div className="flex gap-8 mt-2 pt-2 border-t border-white/10"><span className="text-[#FFE600] font-display uppercase tracking-widest text-[11px]">Total</span><span className="font-display text-2xl">{money(inv.total)}</span></div>
                        <div className="flex gap-8"><span className="text-white/60">Deposit ({inv.deposit_percent}%)</span><span className="text-[#FFE600]">{money(inv.deposit_amount)}</span></div>
                        <div className="flex gap-8"><span className="text-white/60">Balance</span><span>{money(inv.balance_due)}</span></div>
                    </div>
                    {inv.notes && <div className="mt-6 pt-4 border-t border-white/10 font-mono text-[12px] leading-relaxed text-white/70">{inv.notes}</div>}
                    {inv.signature_name && (
                        <div className="mt-6 pt-4 border-t border-white/10">
                            <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Signed by</div>
                            <div className="font-display text-3xl italic mt-1">{inv.signature_name}</div>
                            <div className="font-mono text-[10px] text-white/50 mt-1">{new Date(inv.signed_at).toLocaleString()}</div>
                        </div>
                    )}
                </div>

                <aside className="glass border border-white/10 p-6">
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Bill to</div>
                    <div className="font-display uppercase text-xl mt-2">{client?.name || "—"}</div>
                    <div className="font-mono text-[12px] text-white/70 mt-2 space-y-0.5">
                        {client?.email && <div>{client.email}</div>}
                        {client?.phone && <div>{client.phone}</div>}
                        {client?.address && <div>{client.address}</div>}
                    </div>

                    <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                        <button onClick={send} disabled={sending || !client?.email} data-testid="inv-send"
                            className="w-full inline-flex items-center justify-center gap-2 border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600] hover:text-black transition-colors px-4 py-3 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-40">
                            <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Email to client"}
                        </button>
                        <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="inv-public-link"
                            className="block text-center border border-white/25 hover:border-white transition-colors px-4 py-3 font-mono text-[11px] tracking-[0.3em] uppercase">
                            Open public link →
                        </a>
                        <div className="font-mono text-[10px] text-white/50 break-all">{publicUrl}</div>
                        {msg && <div data-testid="inv-send-msg" className="font-mono text-[11px] text-[#FFE600]">{msg}</div>}
                    </div>
                    <Link to="/crm/invoices" className="mt-6 inline-block font-mono text-[10px] tracking-[0.28em] uppercase text-white/50 hover:text-white">← All invoices</Link>
                </aside>
            </div>
        </div>
    );
}
